import {
  Alert,
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { APP_ALERT_PROGRESS_INTERVAL } from '../constants';
import { getAutoHideDuration, isCurrentInternalRoute } from '../helpers';
import CloseIcon from '@mui/icons-material/Close';
import { FocusEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertEntry } from '../../TypeScript/interfaces';
import { DialogErrorDetails } from '../../Components/Dialogs/DialogErrorDetails';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

export interface AppAlertProps {
  appAlert: AlertEntry;
  isQueueHead: boolean;
  onClose: () => void;
}

export const AppAlert = ({ appAlert, isQueueHead, onClose }: AppAlertProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const currentLocation = useLocation();
  const onCloseRef = useRef(onClose);

  const [isDetailsDialogOpen, setIsDetailsDialogOpen] =
    useState<boolean>(false);
  const [isAutoHidePaused, setIsAutoHidePaused] = useState<boolean>(false);
  const [isDocumentHidden, setIsDocumentHidden] = useState<boolean>(
    document.hidden,
  );

  // Used for rendering the progress of the auto-hide timer
  const [elapsedAutoHideTime, setElapsedAutoHideTime] = useState<number>(0);

  // Used for keeping track of the elapsed auto-hide time without causing re-renders
  const elapsedAutoHideRef = useRef<number>(0);

  // Used to avoid stale closure issues with the onClose callback
  // and unnecessary re-renders if the onClose callback changes
  onCloseRef.current = onClose;

  const autoHideDuration = getAutoHideDuration(appAlert, isQueueHead);

  const isAutoHideActive =
    autoHideDuration !== null && !isAutoHidePaused && !isDocumentHidden;

  // Sets the progress of the auto-hide timer as a percentage of the total duration
  const autoHideProgress =
    autoHideDuration === null
      ? 0
      : Math.min((elapsedAutoHideTime / autoHideDuration) * 100, 100);

  // Used to hide link if the current route is already the target
  const isLinkCurrentRoute = isCurrentInternalRoute(
    currentLocation,
    window.location.origin,
    appAlert.link,
  );

  const handleOpenAlertDetails = () => {
    setIsDetailsDialogOpen(true);
  };

  const handleCloseAlertDetails = () => {
    setIsDetailsDialogOpen(false);
    onCloseRef.current();
  };

  // Handles pausing auto-hide when the document is hidden
  // eg another browser tab is active
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsDocumentHidden(document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Handles the auto-hide timer and updating the elapsed time
  useEffect(() => {
    if (autoHideDuration === null) {
      elapsedAutoHideRef.current = 0;
      setElapsedAutoHideTime(0);
      return;
    }

    if (!isAutoHideActive) {
      return;
    }

    const startedAt = performance.now() - elapsedAutoHideRef.current;

    const updateCountdown = () => {
      const elapsedTime = Math.min(
        performance.now() - startedAt,
        autoHideDuration,
      );
      elapsedAutoHideRef.current = elapsedTime;
      setElapsedAutoHideTime(elapsedTime);
    };

    // Using interval as a throttle to avoid excessive updates to the progress bar
    const intervalId = window.setInterval(
      updateCountdown,
      APP_ALERT_PROGRESS_INTERVAL,
    );

    // Sets up the timeout to auto-hide the alert after the specified duration
    const timeoutId = window.setTimeout(
      () => {
        elapsedAutoHideRef.current = autoHideDuration;
        setElapsedAutoHideTime(autoHideDuration);
        onCloseRef.current();
      },
      Math.max(autoHideDuration - elapsedAutoHideRef.current, 0),
    );

    return () => {
      // Clean up and preserve the elapsed auto-hide time
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);

      const elapsedTime = Math.min(
        performance.now() - startedAt,
        autoHideDuration,
      );
      elapsedAutoHideRef.current = elapsedTime;
    };
  }, [autoHideDuration, isAutoHideActive]);

  const alertStyles = {
    ...(appAlert.severity === 'error' && {
      border: `1px solid ${theme.palette.error.main}`,
    }),
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
  };

  const handleClickAway = () => {
    if (isQueueHead && appAlert.shouldCloseOnClickAway) {
      onCloseRef.current();
    }
  };

  const handleFocusBlur = (event: FocusEvent<HTMLDivElement>) => {
    // Ignore blur events between the alert and its children
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsAutoHidePaused(false);
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        onBlurCapture={handleFocusBlur}
        onFocusCapture={() => setIsAutoHidePaused(true)}
        onMouseEnter={() => setIsAutoHidePaused(true)}
        onMouseLeave={() => setIsAutoHidePaused(false)}
      >
        <Alert
          severity={appAlert.severity}
          sx={alertStyles}
          action={
            <Box display="flex" justifyContent="flex-end" gap={1}>
              {appAlert.details && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleOpenAlertDetails}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('BUTTON_DETAILS')}
                </Button>
              )}
              {appAlert.link && !isLinkCurrentRoute && (
                <Button
                  component={Link}
                  to={appAlert.link}
                  color="inherit"
                  size="small"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('BUTTON_DETAILS')}
                </Button>
              )}
              <IconButton color="inherit" size="small" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          }
        >
          <Stack gap={1}>
            <Box display="flex" flexDirection="row" gap={1}>
              {appAlert.message}
              {appAlert.progress != null && (
                <Typography variant="body2">
                  {Math.round(appAlert.progress)}%
                </Typography>
              )}
            </Box>

            {/* Operation progress */}
            {appAlert.progressMode !== undefined &&
              appAlert.progressMode !== 'none' && (
                <LinearProgress
                  aria-label="Alert operation progress"
                  color="inherit"
                  sx={{
                    bottom: 0,
                    height: 2,
                    left: 0,
                    position: 'absolute',
                    right: 0,
                  }}
                  value={appAlert.progress}
                  variant={appAlert.progressMode}
                />
              )}

            {/* Auto-hide progress */}
            {autoHideDuration !== null && (
              <LinearProgress
                aria-label="Alert auto-close countdown"
                color="inherit"
                sx={{
                  bottom: 0,
                  height: 2,
                  left: 0,
                  position: 'absolute',
                  right: 0,
                }}
                value={autoHideProgress}
                variant="determinate"
              />
            )}
          </Stack>
        </Alert>
        <DialogErrorDetails
          isDetailsDialogOpen={isDetailsDialogOpen}
          onClose={handleCloseAlertDetails}
          severity={appAlert.severity}
          details={appAlert.details || ''}
        />
      </Box>
    </ClickAwayListener>
  );
};
